import type { KDisplayDecimals } from './ioPlanKDecimalsStorage';

export type { KDisplayDecimals };

/**
 * Map an ISO currency code to a display symbol/prefix. Unknown codes fall back
 * to the code itself with a trailing space (e.g. "SEK ") so the currency is
 * always unambiguous rather than defaulting to `$`.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', CHF: 'CHF ',
  CAD: 'CA$', AUD: 'A$', INR: '₹', MXN: 'MX$', BRL: 'R$',
};

export function currencySymbol(code?: string | null): string {
  if (!code) return '$';
  const upper = code.toUpperCase();
  return CURRENCY_SYMBOLS[upper] ?? `${upper} `;
}

/** Format number in thousands: 45000 → "$45K" */
export function fmtK(value: number | null | undefined): string {
  if (value == null) return '-';
  const k = value / 1000;
  const roundedK = Number(k.toFixed(0));
  return `$${roundedK.toLocaleString('en-US')}K`;
}

/** Format number in millions: 1200000 → "$1.2M" */
export function fmtM(value: number | null | undefined): string {
  if (value == null) return '-';
  const m = value / 1_000_000;
  return `$${m.toFixed(1)}M`;
}

/**
 * Format currency with commas.
 * decimals = 'auto' (default): 0 decimal places for whole numbers, 2 for fractional values.
 * decimals = N: always exactly N decimal places.
 * Examples: fmtCurrency(1000) → "$1,000", fmtCurrency(1000.5) → "$1,000.50"
 */
export function fmtCurrency(value: number | null | undefined, decimals: number | 'auto' = 'auto'): string {
  if (value == null) return '-';
  const resolved = decimals === 'auto'
    ? (Number.isInteger(value) ? 0 : 2)
    : decimals;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: resolved,
    maximumFractionDigits: resolved,
  }).format(value);
}

/**
 * Whole-dollar `$K` display. Rounds USD to the nearest dollar, divides by
 * 1000, then rounds to a whole K. Negative amounts render the sign before the
 * `$` (`-$36K`). Example: 36000 → "$36K", 3336000 → "$3,336K", -36000 → "-$36K".
 */
export function fmtKWhole(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '-';
  const k = Math.round(Math.round(usd) / 1000);
  const sign = k < 0 ? '-' : '';
  return `${sign}$${Math.abs(k).toLocaleString('en-US')}K`;
}

/**
 * Whole `$K` display for values already stored in K units (One Pagers).
 * Rounds the K value to a whole number — no ÷1000. Negative amounts render the
 * sign before the `$`. Example: 36 → "$36K", 3336 → "$3,336K", 0 → "$0K".
 */
export function fmtKWholeFromK(k: number | null | undefined): string {
  if (k == null || !Number.isFinite(k)) return '-';
  const rounded = Math.round(k);
  const sign = rounded < 0 ? '-' : '';
  return `${sign}$${Math.abs(rounded).toLocaleString('en-US')}K`;
}

/**
 * Round USD to the granularity implied by `decimals` K places before ÷1000.
 * 3 dp → $1, 5 dp → $0.01, 2 dp → $10, 0 dp → $1,000.
 */
export function roundUsdForKDisplay(usd: number, decimals: number): number {
  const step = Math.pow(10, 3 - decimals);
  return Math.round(usd / step) * step;
}

/**
 * Fixed-width `$K` display. Always shows exactly `decimals` places (trailing zeros kept).
 * Rounds USD to the K-implied granularity, then divides by 1000.
 * Examples: 3100 → "$3.10K", 3123.45 → "$3.12345K" (5 dp), 25792 → "$25.79K" (2 dp).
 */
export function fmtKFixed(
  usd: number | null | undefined,
  decimals: KDisplayDecimals = 2,
  symbol: string = '$',
): string {
  if (usd == null || !Number.isFinite(usd)) return '-';
  const k = roundUsdForKDisplay(usd, decimals) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${sign}${symbol}${formatted}K`;
}

/**
 * `$K` display with up to `maxDecimals` places — trailing zeros stripped.
 * Use for compact summary badges (toolbar totals) so precision setting does
 * not pad with zeros and shift layout. Grid cells should use `fmtKFixed`.
 */
export function fmtKUpTo(
  usd: number | null | undefined,
  maxDecimals: KDisplayDecimals = 2,
  symbol: string = '$',
): string {
  if (usd == null || !Number.isFinite(usd)) return '-';
  const k = roundUsdForKDisplay(usd, maxDecimals) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
  return `${sign}${symbol}${formatted}K`;
}

/**
 * Fixed-width K numeric portion for MonthInput blur display (no `$`/`K` chrome).
 * Empty string for null/undefined so an empty cell renders blank.
 */
export function fmtKFixedInput(
  usd: number | null | undefined,
  decimals: KDisplayDecimals = 2,
): string {
  if (usd == null || !Number.isFinite(usd)) return '';
  const k = roundUsdForKDisplay(usd, decimals) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${sign}${formatted}`;
}

/**
 * Numeric `$K` for spreadsheet exports — dollar-round ÷1000 at fixed dp (pivot-safe).
 */
export function usdToKFixed(
  usd: number | null | undefined,
  decimals: KDisplayDecimals = 2,
): number | null {
  if (usd == null || !Number.isFinite(usd)) return null;
  return Number((roundUsdForKDisplay(usd, decimals) / 1000).toFixed(decimals));
}

/**
 * Smart `$K` display with up to 3 decimal places, trailing zeros stripped.
 * Rounds USD to the nearest dollar first, divides by 1000, then shows only the
 * significant fractional K digits — no padding to a fixed width. Negative
 * amounts render the sign before the `$` (`-$32.56K`), matching `fmtKWhole`.
 * Examples: 65100 → "$65.1K", 32560 → "$32.56K", 25792 → "$25.792K", 32000 → "$32K".
 */
export function fmtK3(usd: number | null | undefined, symbol: string = '$'): string {
  if (usd == null || !Number.isFinite(usd)) return '-';
  const k = Math.round(usd) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
  return `${sign}${symbol}${formatted}K`;
}

/**
 * Smart `$K` value for editable cells — the same numeric portion as `fmtK3`
 * but without the `$` prefix or `K` suffix, which the input UI supplies as
 * chrome. Rounds USD to the nearest dollar, divides by 1000, shows up to 3
 * decimals with trailing zeros stripped. Empty string for null/undefined so an
 * empty cell renders blank.
 * Examples: 65100 → "65.1", 32560 → "32.56", 32000 → "32", -32500 → "-32.5".
 */
export function fmtK3Input(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '';
  const k = Math.round(usd) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
  return `${sign}${formatted}`;
}

/**
 * Raw `$K` string for IO plan month cell editing — capped at `decimals` dp $K,
 * trailing zeros stripped. Uses the same USD rounding as fixed-width display.
 */
export function fmtKPlanInput(
  usd: number | null | undefined,
  decimals: KDisplayDecimals = 2,
): string {
  if (usd == null || !Number.isFinite(usd)) return '';
  if (usd === 0) return '0';
  const k = roundUsdForKDisplay(usd, decimals) / 1000;
  const sign = k < 0 ? '-' : '';
  const formatted = Math.abs(k).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
    useGrouping: false,
  });
  return `${sign}${formatted}`;
}

/**
 * Numeric `$K` value for spreadsheet exports — dollar-round then ÷1000 so the
 * cell stays a number (pivot-safe) at $1 (0.001K) granularity. Same rounding as
 * `fmtK3` without the `$`/`K` display chrome. `null` passes through so an empty
 * cell stays empty. Example: 32560 → 32.56, 25791 → 25.791, 32000 → 32.
 */
export function usdToK(usd: number | null | undefined): number | null {
  if (usd == null || !Number.isFinite(usd)) return null;
  return Math.round(usd) / 1000;
}

/**
 * Maximum decimal places accepted in a `$K` user input. Three decimals of K
 * (0.001K = $1) is dollar-level granularity — finer entry is rejected.
 */
export const K_INPUT_MAX_DECIMALS = 3;

/**
 * True when a user-entered `$K` value carries more than `max` decimal places.
 * Tolerates `$`, `K`/`k`, commas and whitespace so it can validate raw input
 * strings. Used to surface a validation error instead of silently rounding.
 * Examples: "65.1234" → true, "65.123" → false, "65" → false.
 */
export function exceedsKDecimals(
  input: string | number | null | undefined,
  max: number = K_INPUT_MAX_DECIMALS,
): boolean {
  if (input == null || input === '') return false;
  const cleaned = String(input).replace(/[$,\sKk]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex < 0) return false;
  return cleaned.length - dotIndex - 1 > max;
}

/**
 * True when the amount carries a non-zero cents portion (i.e. it is not a whole
 * dollar). Used by BI mismatch tooltips to decide between full-cent and `$K`
 * rendering so cent-level variance is never rounded away.
 */
export function hasFractionalCents(usd: number | null | undefined): boolean {
  if (usd == null || !Number.isFinite(usd)) return false;
  return Math.round(usd * 100) % 100 !== 0;
}

/**
 * BI mismatch tooltip amount. Shows full currency with cents when the amount
 * has a fractional-cent (sub-dollar) part; otherwise uses smart `$K`.
 * Examples: 32950.5 → "$32,950.50", 0.47 → "$0.47", 32950 → "$32.950K", 1000 → "$1K".
 */
export function fmtBiTooltipAmount(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '-';
  return hasFractionalCents(usd) ? fmtCurrency(usd) : fmtK3(usd);
}

/**
 * Parse a user-entered `$K` value into USD stored at 2-decimal precision.
 * Accepts strings with `$`, `K`/`k`, commas and surrounding whitespace.
 * Example: "$3K" → 3000, "32.56" → 32560. Returns null for empty/invalid input.
 */
export function parseKToUsd(kInput: string | number | null | undefined): number | null {
  if (kInput == null || kInput === '') return null;
  const raw = typeof kInput === 'number' ? String(kInput) : kInput;
  const cleaned = raw.replace(/[$,\sKk]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const k = Number(cleaned);
  if (!Number.isFinite(k)) return null;
  return Math.round(k * 1000 * 100) / 100;
}

/**
 * Format a calendar date string (YYYY-MM-DD) without timezone shifting.
 * Parses as UTC-midnight so users west of UTC don't see the day roll back by one.
 * Example: "2026-05-01" → "May 1, 2026"
 */
export function formatDate(value: string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return '-';
  const d = new Date(value.includes('T') ? value : value + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    timeZone: 'UTC',
    ...options,
  }).format(d);
}

/**
 * Format a UTC timestamp (ISO string, with or without trailing Z) in the
 * user's local timezone. Appends Z if the string has no offset so the
 * browser doesn't misparse it as local time.
 * Example: "2026-05-01T18:00:00Z" → "May 1, 2026, 11:00 AM PDT"
 */
export function formatDateTime(value: string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return '-';
  const utc = /[Z+\-]\d{2}:?\d{2}$|Z$/i.test(value) ? value : value + 'Z';
  const d = new Date(utc);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    ...options,
  }).format(d);
}

/**
 * Compact absolute timestamp with the viewer's timezone abbreviation.
 *
 * Used for governance / audit timestamps (Submit/Lock) where ambiguity is
 * unacceptable. Produces e.g. `2026-04-28 02:19 GMT+5` so a viewer in
 * Karachi and a viewer in NYC each see their own local wall-clock time
 * with their own zone label, never a bare number that could be misread.
 *
 * Backend serializes `datetime.utcnow()` to ISO without a trailing `Z`,
 * which JavaScript parses as local time — wrong by the user's UTC offset.
 * We append `Z` defensively so the input is always treated as UTC and
 * then converted into the viewer's zone for display.
 */
export function formatStamp(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const utcIso = /[Z+-]\d{2}:?\d{2}$|Z$/i.test(isoString) ? isoString : isoString + 'Z';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  try {
    const tz = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(d)
      .find(p => p.type === 'timeZoneName')?.value;
    return tz ? `${date} ${tz}` : date;
  } catch {
    return date;
  }
}

export function formatTimeAgo(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const utc = isoString.endsWith('Z') ? isoString : isoString + 'Z';
  const d = new Date(utc);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** User-facing IO type label (API may send Opex/Capex in any casing). */
export function formatIoTypeLabel(value: string | null | undefined): string {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  if (normalized === 'opex') return 'OPEX';
  if (normalized === 'capex') return 'CAPEX';
  return value.trim();
}

/** Standalone opex/capex words inside API sentences (warnings, toasts, dashboard). */
export function normalizeOpexCapexWordsInText(text: string): string {
  return text.replace(/\b(opex|capex)\b/gi, (m) => m.toUpperCase());
}

/** Escape HTML to prevent XSS */
export function escapeHtml(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
